import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Box, Tab, Tabs } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  useGetAriCaseDetail,
  useGetAriCaseSummary,
  useGetAriCases,
  useGetAriKnownIssues,
  useGetAriRules,
  useSuppressAriCase,
  useTransitionAriCase,
} from '~/hooks/apis/reporting/useAriAnomalies';

import { RollupDimension, SuppressionInput } from '~/services/AriAnomaliesService';

import CaseDetailModal from './CaseDetailModal';
import CaseList, { CaseFilters } from './CaseList';
import CaseRollup from './CaseRollup';
import DismissCaseDialog from './DismissCaseDialog';
import SuppressCaseDialog from './SuppressCaseDialog';
import { suppressionMessage } from './suppressionMessage';
import { AnomalyCase, CaseStatus } from './types';

const DEFAULT_ROWS_PER_PAGE = 50;

/**
 * Two tabs, one dataset, one place to act.
 *
 * Cases is the work queue. Rollup answers "where is the damage concentrated" and hands back
 * to a filtered queue - it deliberately cannot triage, so there is exactly one surface where
 * a case changes state.
 */
export default function AriAnomaliesPage() {
  const [tab, setTab] = useState<'cases' | 'rollup'>('cases');
  const [filters, setFilters] = useState<CaseFilters>({
    status: 'pending',
    verdict: 'all',
    tier: 'all',
    ruleKeys: [],
    dimension: null,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [openCaseId, setOpenCaseId] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<AnomalyCase | null>(null);
  const [suppressing, setSuppressing] = useState<AnomalyCase | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const query = useMemo(() => ({ ...filters, page: page + 1, limit: rowsPerPage }), [filters, page, rowsPerPage]);

  const { data: casePage, isLoading, isFetching, error } = useGetAriCases(query);
  const { data: summary } = useGetAriCaseSummary();
  const { data: rules = [] } = useGetAriRules();
  const { data: knownIssues = [] } = useGetAriKnownIssues();
  const { data: detail } = useGetAriCaseDetail(openCaseId);
  const transition = useTransitionAriCase();
  const suppress = useSuppressAriCase();

  const cases = useMemo(() => casePage?.cases ?? [], [casePage]);

  // The list endpoint carries the case, never its products, timeline or checks; those load
  // when a case is opened and are merged in here so the modal keeps one shape.
  const openCase = useMemo(() => {
    const found = cases.find((c) => c.caseId === openCaseId);
    if (!found) {
      return null;
    }
    if (!detail) {
      return found;
    }
    return {
      ...found,
      affectedProducts: detail.affectedProducts,
      history: detail.history,
      preValidation: { ...found.preValidation, verdict: detail.verdict, ranAt: detail.ranAt, checks: detail.checks },
    };
  }, [cases, openCaseId, detail]);

  const changeFilters = useCallback((next: CaseFilters) => {
    setFilters(next);
    setPage(0);
  }, []);

  const applyStatus = useCallback(
    (caseId: string, status: CaseStatus, reasonKey?: string, note?: string) => {
      transition.mutate(
        { caseId, status, body: { reason_key: reasonKey, note } },
        {
          onSuccess: (updated) => {
            enqueueSnackbar(
              updated.dismissReason ? `${caseId} ${status}: ${updated.dismissReason}` : `${caseId} ${status}`,
              { variant: status === 'dismissed' ? 'warning' : 'success' },
            );
          },
          onError: (err: Error) => {
            enqueueSnackbar(`${caseId} could not be ${status}: ${err.message}`, { variant: 'error' });
          },
        },
      );
    },
    [enqueueSnackbar, transition],
  );

  /**
   * The wording says what was silenced, not that this case changed: a suppression only moves
   * cases that are still pending or acknowledged, so claiming a status change here would be
   * wrong for a case that was already resolved or dismissed.
   */
  const applySuppression = useCallback(
    (input: SuppressionInput) => {
      suppress.mutate(input, {
        onSuccess: (created) => {
          enqueueSnackbar(suppressionMessage(input.days, input.scope, created.casesSuppressed), {
            variant: 'warning',
          });
        },
        onError: (err: Error) => {
          enqueueSnackbar(`Could not suppress: ${err.message}`, { variant: 'error' });
        },
      });
    },
    [enqueueSnackbar, suppress],
  );

  const handleDrillIn = useCallback(
    (dimension: RollupDimension, value: string) => {
      changeFilters({
        status: 'pending',
        verdict: 'all',
        tier: 'all',
        ruleKeys: [],
        dimension: { field: dimension, value },
      });
      setTab('cases');
    },
    [changeFilters],
  );

  if (error) {
    return (
      <Box>
        <PageSubheader title="ARI Anomalies" />
        <Alert severity="error">Could not load ARI cases: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageSubheader title="ARI Anomalies" />

      <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 2 }}>
        <Tab value="cases" label="Cases" />
        <Tab value="rollup" label="Rollup" />
      </Tabs>

      {tab === 'cases' && (
        <CaseList
          cases={cases}
          total={casePage?.total ?? 0}
          rules={rules}
          summary={summary}
          isLoading={isLoading}
          isFetching={isFetching}
          filters={filters}
          onFiltersChange={changeFilters}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onOpenCase={(c) => setOpenCaseId(c.caseId)}
          onStatusChange={applyStatus}
          onDismiss={setDismissing}
          onSuppress={setSuppressing}
        />
      )}

      {tab === 'rollup' && <CaseRollup onDrillIn={handleDrillIn} />}

      <CaseDetailModal
        anomalyCase={openCase}
        onClose={() => setOpenCaseId(null)}
        onStatusChange={applyStatus}
        onDismiss={setDismissing}
        onSuppress={setSuppressing}
      />

      <DismissCaseDialog
        anomalyCase={dismissing}
        knownIssues={knownIssues}
        onClose={() => setDismissing(null)}
        onConfirm={(caseId, reasonKey, note) => {
          applyStatus(caseId, 'dismissed', reasonKey, note);
          setDismissing(null);
        }}
      />

      <SuppressCaseDialog
        anomalyCase={suppressing}
        onClose={() => setSuppressing(null)}
        onConfirm={(input) => {
          applySuppression(input);
          setSuppressing(null);
        }}
      />
    </Box>
  );
}
